'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { buildBreadcrumbManager } from '@coveo/atomic-react';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useController } from '@lib/hooks/useController';
import { useSearchParams } from 'next/navigation';
import { XCircle } from 'phosphor-icons';
import { useCallback, useMemo, useState } from 'react';
import { c } from 'ui-components';
import { useEngine } from '../EngineContext';
import {
  SALE_LABEL_FALLBACKS,
  SALE_MICROCOPY_MAP,
  getDateRangeLabel,
  getNumericRangeLabel,
} from '../facetControllers';
import { FacetBreadcrumbsProps } from './FacetBreadcrumbs.types';

const MAX_VISIBLE_BREADCRUMBS = 5;

const FIELD_LABELS: Record<string, string> = {
  ec_category: 'Category',
  ec_price_filter: 'Price',
  ec_rating: 'Rating',
  min_unity_version: 'Unity Version',
  ec_sale_filters: 'Sale',
  publisher_name: 'Publisher',
  first_published_at: 'Release Date',
  ec_ownership: 'Ownership',
};

export const FacetBreadcrumbs = ({ className }: FacetBreadcrumbsProps) => {
  const searchParams = useSearchParams();
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.breadcrumbs.clear')
      ?.value ?? 'Clear';
  const showMoreLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.breadcrumbs.show-more')
      ?.value ?? 'Show more';
  const showLessLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.breadcrumbs.show-less')
      ?.value ?? 'Show less';
  const ownershipLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.ownership.hide-owned')
      ?.value ?? 'Hide owned assets';

  const eng = useEngine();
  const controller = useMemo(() => buildBreadcrumbManager(eng), [eng]);
  const state = useController(controller);

  const [showAll, setShowAll] = useState(false);

  const isOwnershipActive = searchParams?.get('hideOwnership') === 'true';

  const handleDeselectOwnership = useCallback(() => {
    if (typeof window === 'undefined') return;
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('hideOwnership');
    window.history.pushState({}, '', currentUrl);
    // Remove DOM attribute to unhide owned assets
    const el = document.querySelector(
      '[data-test="search-result-section"]'
    ) as HTMLElement | null;
    el?.removeAttribute('data-hide-owned');
  }, []);

  const allBreadcrumbs = useMemo(() => {
    const items: { field: string; label: string; deselect: () => void }[] = [];

    const formatLabel = (field: string, value: string) => {
      const fieldLabel = FIELD_LABELS[field];
      return fieldLabel ? `${fieldLabel}: ${value}` : value;
    };

    const getSaleLabel = (value: string) => {
      const microcopyKey = SALE_MICROCOPY_MAP[value];
      if (microcopyKey) {
        const entry = searchLabelsMicrocopy?.find(
          (m) => m.key === microcopyKey
        );
        if (entry?.value) return entry.value;
      }
      return SALE_LABEL_FALLBACKS[value] ?? value;
    };

    state.facetBreadcrumbs
      ?.filter((facet) => facet.field !== 'ec_category')
      .forEach((facet) =>
        facet.values.forEach((breadcrumb) => {
          const displayValue =
            facet.field === 'ec_sale_filters'
              ? getSaleLabel(breadcrumb.value.value)
              : breadcrumb.value.value;
          items.push({
            field: facet.field,
            label: formatLabel(facet.field, displayValue),
            deselect: breadcrumb.deselect,
          });
        })
      );

    state.numericFacetBreadcrumbs?.forEach((facet) =>
      facet.values.forEach((breadcrumb) => {
        const rangeLabel = getNumericRangeLabel(
          breadcrumb.value.start,
          breadcrumb.value.end
        );
        items.push({
          field: facet.field,
          label: formatLabel(facet.field, rangeLabel),
          deselect: breadcrumb.deselect,
        });
      })
    );

    state.dateFacetBreadcrumbs?.forEach((facet) =>
      facet.values.forEach((breadcrumb) => {
        const rangeLabel = getDateRangeLabel(
          breadcrumb.value.start,
          breadcrumb.value.end
        );
        items.push({
          field: facet.field,
          label: formatLabel(facet.field, rangeLabel),
          deselect: breadcrumb.deselect,
        });
      })
    );

    // Ownership (custom filter, not a Coveo facet)
    if (isOwnershipActive) {
      items.push({
        field: 'ownership',
        label: `Ownership: ${ownershipLabel}`,
        deselect: handleDeselectOwnership,
      });
    }

    return items;
  }, [
    state.facetBreadcrumbs,
    state.numericFacetBreadcrumbs,
    state.dateFacetBreadcrumbs,
    isOwnershipActive,
    ownershipLabel,
    handleDeselectOwnership,
    searchLabelsMicrocopy,
  ]);

  const handleClearAll = useCallback(() => {
    controller.deselectAll();
    if (isOwnershipActive) {
      handleDeselectOwnership();
    }
  }, [controller, isOwnershipActive, handleDeselectOwnership]);

  if (allBreadcrumbs.length === 0) {
    return null;
  }

  const visibleBreadcrumbs = showAll
    ? allBreadcrumbs
    : allBreadcrumbs.slice(0, MAX_VISIBLE_BREADCRUMBS);
  const hasMore = allBreadcrumbs.length > MAX_VISIBLE_BREADCRUMBS;

  return (
    <div
      className={c('flex flex-wrap items-center gap-2', className)}
      data-test="facet-breadcrumbs"
    >
      {visibleBreadcrumbs.map((breadcrumb, index) => (
        <button
          key={`${breadcrumb.field}-${breadcrumb.label}-${index}`}
          type="button"
          onClick={breadcrumb.deselect}
          aria-label={`Remove ${breadcrumb.label} filter`}
          className="flex items-center gap-1 rounded-full bg-grey-100 py-1 pl-3 pr-2 text-sm font-medium leading-5 text-grey-700"
        >
          {breadcrumb.label}
          <XCircle size={16} className="shrink-0 text-grey-500" />
        </button>
      ))}

      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll((prev) => !prev)}
          className="px-1.5 py-1.5 text-[13px] font-medium leading-5 text-grey-500"
        >
          {showAll ? showLessLabel : showMoreLabel}
        </button>
      )}

      <button
        type="button"
        onClick={handleClearAll}
        className="px-1.5 py-1.5 text-[13px] font-medium leading-5 text-grey-500"
      >
        {clearLabel}
      </button>
    </div>
  );
};
